// 必须在任何 preact / tdesign-react / stuck-reporter 依赖之前求值。

// 老 WebKit（Mac 版腾讯会议内嵌 WKWebView 基于此）缺失 Object.is。
if (typeof Object.is !== 'function') {
  Object.defineProperty(Object, 'is', {
    value: function is(x, y) {
      if (x === y) {
        return x !== 0 || 1 / x === 1 / y;
      }
      return x !== x && y !== y;
    },
    configurable: true,
    writable: true,
  });
}

// preact diffElementNodes 会直接读 newVNode.props.is，遇到 props=undefined 就抛
// "undefined is not an object (evaluating 'f.is')"，页面卡加载页。
// preact 有 options.vnode hook（未 mangle，minified 后仍叫 vnode），所有 createVNode 出口都会调它 —— 
// 包括 h()、cloneElement()、以及 diff/children.js 里"从旧 vnode 复制"这条 preact 内部路径
// （这条路径不走 h()，是我们上一版 h() 兜底漏掉的地方）。
try {
  const { options } = require('preact');
  const originalVnodeHook = options.vnode;
  let patchedCount = 0;
  let reportedOnce = false;

  // preact minified 后的 mangle 表（见 node_modules/preact/dist/preact.min.js 中 createVNode）：
  //   _parent   → __
  //   _depth    → __b
  //   _children → __k
  // 直接访问点号属性走 mangled 名；一旦 preact 大版本改 mangle 规则此处会读到 undefined，
  // 但不影响兜底（patch props 的核心逻辑不依赖这些字段）。
  function getPreactParent(v) { try { return v.__; } catch (_e) { return null; } }
  function getPreactDepth(v)  { try { return v.__b; } catch (_e) { return undefined; } }
  function getPreactChildren(v) { try { return v.__k; } catch (_e) { return null; } }

  function nameOfType(t) {
    if (typeof t === 'string') return t;
    if (!t) return typeof t;
    return t.displayName || t.name || String(t).slice(0, 40);
  }

  // 向上回溯最多 5 层父 vnode 的 type.name，用 → 拼接。
  // 根因挖掘用：当栈里全是 preact 内部帧时，父链的组件名列表能定位到具体业务组件。
  function collectAncestry(vnode) {
    const chain = [];
    let cur = vnode;
    for (let i = 0; i < 6 && cur; i++) {
      try {
        chain.push(nameOfType(cur.type));
      } catch (_e) {
        chain.push('<throw>');
        break;
      }
      cur = getPreactParent(cur);
    }
    return chain.join('→');
  }

  // 判别 WebView 类型：Mac 腾讯会议是 WKWebView（AppleWebKit 无 Chrome 字段），
  // Windows 腾讯会议是 CEF（含 Chrome/XXX），用于 Seg 聚合分析。
  function detectWebviewKind() {
    try {
      const ua = navigator.userAgent || '';
      const isWemeet = /tencent_wemeet/i.test(ua);
      const hasChrome = /Chrome\/\d+/.test(ua);
      if (isWemeet && !hasChrome) return 'wkwebview-wemeet';
      if (isWemeet && hasChrome) return 'cef-wemeet';
      if (!hasChrome && /AppleWebKit/.test(ua)) return 'wkwebview-other';
      if (hasChrome) return 'chromium';
      return 'other';
    } catch (_e) {
      return 'unknown';
    }
  }

  options.vnode = function (vnode) {
    if (vnode && vnode.props == null) {
      patchedCount += 1;
      vnode.props = {};

      if (!reportedOnce) {
        reportedOnce = true;
        const capturedType = vnode.type;
        const snapshotCount = patchedCount;
        // 必须同步抓栈：异步阶段的 err.stack 只会停在 setTimeout 回调自己，
        // 丢掉 preact createVNode / 业务 h() 这条真正定位根因的调用链。
        const err = new Error('vnode_undefined_props');
        const stack = err && err.stack ? String(err.stack).split('\n').slice(0, 30).join('\n') : '';
        const typeOfVnodeType = typeof capturedType;
        let vnodeKeys = '';
        try {
          vnodeKeys = Object.keys(vnode).slice(0, 20).join(',');
        } catch (_e) {
          // 某些 Proxy 场景 Object.keys 会抛，忽略
        }
        const vnodeAncestry = collectAncestry(vnode);
        const vnodeDepth = getPreactDepth(vnode);
        let parentChildrenCount = -1;
        try {
          const parentChildren = getPreactChildren(getPreactParent(vnode));
          if (parentChildren && typeof parentChildren.length === 'number') {
            parentChildrenCount = parentChildren.length;
          }
        } catch (_e) {
          // 父节点或 children 读取抛错 → 保持 -1
        }
        const webviewKind = detectWebviewKind();
        // 异步上报，避免在 render 路径里同步做 IO
        setTimeout(function () {
          try {
            const stuck = require('@cloudstudio/stuck-reporter');
            const vnodeType =
              typeof capturedType === 'string'
                ? capturedType
                : (capturedType && (capturedType.displayName || capturedType.name)) ||
                  String(capturedType).slice(0, 60);
            if (stuck.mark) {
              stuck.mark('preact_vnode_undefined_props', {
                vnodeType,
                typeOfVnodeType,
                vnodeKeys,
                vnodeAncestry,
                vnodeDepth,
                parentChildrenCount,
                webviewKind,
                stack,
                patchedCount: snapshotCount,
              });
            }
            if (stuck.flush) stuck.flush('preact_undefined_props');
          } catch (_e) {
            // 兜底流程绝不能再抛
          }
        }, 0);
      }
    }
    if (originalVnodeHook) originalVnodeHook(vnode);
  };

  window.__preactPatchStats = function () {
    return { patched: patchedCount };
  };
} catch (_e) {
  // 拿不到 preact 就不 patch，不影响主流程
}
